/** Stream user id from email (matches api/login.js). */
export function streamUserIdFromEmail(email) {
  return email.toLowerCase().replaceAll(/[^a-z]/g, '-')
}

/** Pick a Stream peer id for a new reservation conversation. */
export function streamUserIdForReservationCustomer({ email, phone, name }) {
  if (email) {
    return streamUserIdFromEmail(email)
  }

  const digits = phone && String(phone).replace(/\D/g, '')
  if (digits) {
    return `phone-${digits}`
  }

  const slug =
    name &&
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')

  if (slug) {
    return `guest-${slug}-${Math.random().toString(16).slice(2, 8)}`
  }

  return `guest-${Math.random().toString(16).slice(2)}`
}

/** Email used to upsert a Stream user when the customer has no address. */
export function streamEnsureEmailForCustomerId(customerId) {
  return `${customerId}@guest.doggos.local`
}

/** True when `?c=` refers to a per-reservation Stream channel, not a user id. */
export function isReservationChannelId(channelId) {
  return typeof channelId === 'string' && channelId.startsWith('reservation-')
}

/** Title shown when a conversation has no personal identifying info to display. */
export const NEW_CHAT_TITLE = 'New chat'

/**
 * True when `value` is a raw Stream/channel identifier (e.g. Stream's default
 * members-based id `!members-...`, a `guest-*` / `phone-*` / `reservation-*`
 * peer id, or a bare random-hex id) rather than a human-friendly label. Such
 * values must never be shown as a conversation title.
 */
export function isRawConversationId(value) {
  if (typeof value !== 'string') {
    return false
  }
  const v = value.trim()
  if (!v) {
    return false
  }
  if (
    v.startsWith('!members-') ||
    v.startsWith('guest-') ||
    v.startsWith('phone-') ||
    v.startsWith('reservation-') ||
    v.startsWith('cal-res-')
  ) {
    return true
  }
  // Bare random-hex ids (e.g. the `?c=` default and the guest fallback id).
  return /^[0-9a-f]{8,}$/i.test(v)
}

/** Readable phone label from a `phone-<digits>` Stream user id, else ''. */
export function formatPhoneFromStreamId(id) {
  if (typeof id !== 'string') {
    return ''
  }
  const match = id.trim().match(/^phone-(\d{7,15})$/)
  if (!match) {
    return ''
  }
  const digits = match[1]
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1)
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  }
  return `+${digits}`
}

/**
 * Pick the first human-friendly label from ordered `candidates`, skipping blanks,
 * the peer id itself, and raw ids. Falls back to "New chat" — never the raw
 * channel/user id. Pass candidates in priority order (name > email > phone).
 */
export function resolveConversationTitle(candidates, options = {}) {
  const peerId =
    typeof options.peerId === 'string' ? options.peerId.trim() : undefined
  const list = Array.isArray(candidates) ? candidates : [candidates]
  for (const candidate of list) {
    if (typeof candidate !== 'string') {
      continue
    }
    const v = candidate.trim()
    if (!v) {
      continue
    }
    if (peerId && v === peerId) {
      continue
    }
    if (isRawConversationId(v)) {
      continue
    }
    return v
  }
  return NEW_CHAT_TITLE
}

/** Human label for a reservation channel (breadcrumb / sidebar). */
export function reservationChannelTitle(data = {}) {
  const name = data.customer?.name?.trim()
  const code = data.code
  if (name && code) {
    return `${name} · #${code}`
  }
  if (name) {
    return name
  }
  if (code) {
    return `Reservation #${code}`
  }
  return 'Reservation'
}
